import type { AuditRecord } from '../data/audits';
import { easternIsoDay } from '../data/taskRules';
import { easternMidnightIso } from '../data/timeZones';
import type { TemplateDefinition } from './template';

/**
 * Analytics aggregation (Phase 6b) — pure functions over submitted Audits
 * rows (score, section scores, failure summary). Nothing here reads
 * AuditResponses: per-question results come from the FailureSummary written
 * at submit (src/audit/failureSummary.ts).
 */

/**
 * CALENDAR months, this one included (owner ruling 2026-10-22 — the monthly
 * summaries answer in whole months): '3m' = this month and the 2 before.
 */
export type Period = '3m' | '6m' | '12m' | 'all';

export const PERIOD_LABEL: Record<Period, string> = {
  '3m': 'Last 3 months',
  '6m': 'Last 6 months',
  '12m': 'Last 12 months',
  all: 'All time',
};

const PERIOD_MONTHS: Record<Exclude<Period, 'all'>, number> = { '3m': 3, '6m': 6, '12m': 12 };

/** Inclusive start (ISO) of a period — Eastern midnight on the 1st of its first month — or '' for all time. */
export function periodStart(period: Period, now: Date = new Date()): string {
  if (period === 'all') return '';
  const [y, m] = easternIsoDay(now).split('-').map(Number) as [number, number];
  const first = new Date(Date.UTC(y, m - 1 - (PERIOD_MONTHS[period] - 1), 1)).toISOString().slice(0, 10);
  return easternMidnightIso(first);
}

export function inPeriod(a: AuditRecord, start: string): boolean {
  return !start || (a.submittedAt !== '' && a.submittedAt >= start);
}

const mean = (xs: readonly number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

// --- Score trend -------------------------------------------------------------

export interface TrendSeries {
  key: string;
  label: string;
  /** One point per month that has audits: month = 'YYYY-MM'. */
  points: { month: string; avg: number; count: number }[];
}

/** Eastern (company calendar) 'YYYY-MM' of an ISO timestamp ('' if unreadable). */
export function monthOf(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return easternIsoDay(d).slice(0, 7);
}

/** Every month from first to last, so gaps show as gaps, not as a slope. */
export function monthRange(first: string, last: string): string[] {
  const out: string[] = [];
  if (!first || !last || first > last) return out;
  let [y, m] = first.split('-').map(Number) as [number, number];
  for (let guard = 0; guard < 600; guard++) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    out.push(key);
    if (key === last) break;
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return out;
}

/**
 * Average score per month, one series per group (e.g. per district). Audits
 * without a score (all N/A) are left out.
 */
export function scoreTrend(
  audits: readonly AuditRecord[],
  groupOf: (a: AuditRecord) => { key: string; label: string } | null,
): TrendSeries[] {
  const groups = new Map<string, { label: string; byMonth: Map<string, number[]> }>();
  for (const a of audits) {
    if (a.score === null) continue;
    const g = groupOf(a);
    const month = monthOf(a.submittedAt);
    if (!g || !month) continue;
    const entry = groups.get(g.key) ?? { label: g.label, byMonth: new Map<string, number[]>() };
    entry.byMonth.set(month, [...(entry.byMonth.get(month) ?? []), a.score]);
    groups.set(g.key, entry);
  }
  return [...groups.entries()]
    .map(([key, { label, byMonth }]) => ({
      key,
      label,
      points: [...byMonth.entries()]
        .sort(([m1], [m2]) => m1.localeCompare(m2))
        .map(([month, scores]) => ({ month, avg: mean(scores)!, count: scores.length })),
    }))
    .sort((x, y) => x.label.localeCompare(y.label));
}

// --- Sections ------------------------------------------------------------------

export interface SectionStat {
  sectionId: string;
  title: string;
  avg: number;
  audits: number;
}

/**
 * Average section score across audits (sections an audit hid or had nothing
 * applicable in — null — don't count). Weakest first. Only sections some
 * template version actually has, and only 0–100 scores: SectionScores is
 * member-writable (risk #1), so junk keys must not turn into thousands of
 * bars or skew the averages.
 */
export function sectionAverages(audits: readonly AuditRecord[], titleOf: (sectionId: string) => string | undefined): SectionStat[] {
  const by = new Map<string, { title: string; scores: number[] }>();
  for (const a of audits) {
    const raw = a.sectionScores;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    for (const [id, score] of Object.entries(raw)) {
      if (typeof score !== 'number' || !Number.isFinite(score) || score < 0 || score > 100) continue;
      const title = titleOf(id);
      if (title === undefined) continue;
      const entry = by.get(id) ?? { title, scores: [] };
      entry.scores.push(score);
      by.set(id, entry);
    }
  }
  return [...by.entries()]
    .map(([sectionId, { title, scores }]) => ({
      sectionId,
      title,
      avg: mean(scores)!,
      audits: scores.length,
    }))
    .sort((x, y) => x.avg - y.avg || x.title.localeCompare(y.title));
}

// --- Questions ----------------------------------------------------------------

export interface QuestionStat {
  questionId: string;
  /** Checked items: 1 per audit for a question, every entry's check for a sample group. */
  checked: number;
  failed: number;
  failRate: number;
  /** Audits that answered it at all. */
  audits: number;
}

/**
 * Failure rate per question from the stored summaries, highest first. Audits
 * without a summary (submitted before 6b) are skipped — the page says how
 * many. `minAudits` keeps one-off failures from topping the list.
 */
export function questionFailureRates(audits: readonly AuditRecord[], minAudits = 1): QuestionStat[] {
  const by = new Map<string, { checked: number; failed: number; audits: number }>();
  const add = (id: string, passed: number, failed: number) => {
    const s = by.get(id) ?? { checked: 0, failed: 0, audits: 0 };
    s.checked += passed + failed;
    s.failed += failed;
    s.audits += 1;
    by.set(id, s);
  };
  for (const a of audits) {
    const sum = a.failureSummary;
    if (!sum) continue;
    for (const [id, r] of Object.entries(sum.q)) add(id, r === 'p' ? 1 : 0, r === 'f' ? 1 : 0);
    for (const [id, [p, f]] of Object.entries(sum.g)) add(id, p, f);
  }
  return [...by.entries()]
    .filter(([, s]) => s.audits >= minAudits && s.checked > 0)
    .map(([questionId, s]) => ({ questionId, ...s, failRate: s.failed / s.checked }))
    .sort((x, y) => y.failRate - x.failRate || y.failed - x.failed || x.questionId.localeCompare(y.questionId));
}

// --- Auditors ----------------------------------------------------------------

export interface AuditorStat {
  auditorUpn: string;
  audits: number;
  avgScore: number | null;
  criticalFails: number;
  stores: number;
  lastSubmitted: string;
}

/** Per-auditor counts and averages, most audits first. UPNs compared trimmed + case-insensitive. */
export function auditorStats(audits: readonly AuditRecord[]): AuditorStat[] {
  const by = new Map<string, { upn: string; list: AuditRecord[] }>();
  for (const a of audits) {
    const upn = a.auditorUpn.trim();
    if (!upn) continue;
    const key = upn.toLowerCase();
    const entry = by.get(key) ?? { upn, list: [] };
    entry.list.push(a);
    by.set(key, entry);
  }
  return [...by.values()]
    .map(({ upn, list }) => ({
      auditorUpn: upn,
      audits: list.length,
      avgScore: mean(list.flatMap((a) => (a.score === null ? [] : [a.score]))),
      criticalFails: list.filter((a) => a.criticalFailed).length,
      stores: new Set(list.map((a) => a.storeId)).size,
      lastSubmitted: list.reduce((m, a) => (a.submittedAt > m ? a.submittedAt : m), ''),
    }))
    .sort((x, y) => y.audits - x.audits || x.auditorUpn.localeCompare(y.auditorUpn));
}

// --- Labels from template definitions ------------------------------------------

export interface TemplateLabels {
  question: Map<string, { text: string; section: string }>;
  section: Map<string, string>;
}

/**
 * Question/section names from every PUBLISHED or retired template version,
 * newest version winning — ids are stable across versions, so a renamed
 * question shows its current wording for its whole history. Drafts are left
 * out (half-edited wording nobody has published).
 */
export function templateLabels(
  defs: readonly { version: number; status?: string; definition: TemplateDefinition | null }[],
): TemplateLabels {
  const question = new Map<string, { text: string; section: string }>();
  const section = new Map<string, string>();
  const usable = defs.filter((d) => d.status !== 'Draft');
  for (const { definition } of [...usable].sort((a, b) => a.version - b.version)) {
    for (const s of definition?.sections ?? []) {
      section.set(s.id, s.title);
      for (const q of s.questions ?? []) question.set(q.id, { text: q.text, section: s.title });
    }
  }
  return { question, section };
}

// --- Test fixtures --------------------------------------------------------------

/**
 * The permanent E2E fixture district (owner ruling 2026-10-17): its stores
 * are left out of dashboards and analytics so automated test audits never
 * reach company numbers. Matched trimmed + case-insensitively.
 */
export const TEST_DISTRICT = 'Test District';

export function isTestStore(store: { district: string }): boolean {
  return store.district.trim().toLowerCase() === TEST_DISTRICT.toLowerCase();
}

/**
 * A Phase 7 simulation district ("[SIM] District 1000"…). Shown everywhere
 * in the app (owner ruling), but never in a scheduled report's "All
 * districts" — Flow 4 skips them the same way (owner ruling 2026-10-20).
 */
export function isSimDistrict(name: string): boolean {
  return name.trim().toLowerCase().startsWith('[sim]');
}

/**
 * Stores for dashboards/analytics: test stores removed — unless they are ALL
 * the viewer can see (the test account itself), so nobody gets an empty page.
 */
export function withoutTestStores<T extends { district: string }>(stores: readonly T[]): T[] {
  const real = stores.filter((s) => !isTestStore(s));
  return real.length > 0 ? real : [...stores];
}
